import React from "react";
import { useExperimentStarted } from "@/hooks/analytics/useExperimentConversion";
import { useGetActiveExperimentVariant } from "@/hooks/analytics/useGetActiveExperimentVariant";
import { useUserAnalyticsId } from "@/hooks/analytics/useUserAnalyticsId";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";

const ExperimentParentContext = React.createContext<boolean>(false);

type VariantProps = {
  code: string;
  children: React.ReactNode;
};

export const Variant = ({ children }: VariantProps) => {
  const hasExperimentParent = React.useContext(ExperimentParentContext);

  // simple console error to warn engineers if they try to use <Variant/> without the proper <Experiment/> parent
  if (!hasExperimentParent && typeof window !== "undefined") {
    console.error(
      "Error: <Variant /> must be a direct child of <Experiment />. " +
        "Please ensure it's not wrapped in other components."
    );
  }

  return <>{children}</>;
};

type ExperimentProps = {
  code: string;
  medspaId: string;
  children: React.ReactElement<VariantProps>[];
};

export const Experiment = ({ code, medspaId, children }: ExperimentProps) => {
  const userAnalyticsId = useUserAnalyticsId();
  const { trackExperimentStarted } = useExperimentStarted();

  const activeVariant = useGetActiveExperimentVariant(code, medspaId);

  // Important: only send segment event once, ensure we have all variant/user data first
  useEffectOnce(!!code && !!activeVariant && !!userAnalyticsId, () => {
    trackExperimentStarted(code, activeVariant);
  });

  if (!activeVariant) return null;

  const activeChild = React.Children.toArray(children).find(
    (child) =>
      React.isValidElement<VariantProps>(child) &&
      child.props.code === activeVariant
  );

  return (
    <ExperimentParentContext.Provider value>
      {activeChild}
    </ExperimentParentContext.Provider>
  );
};
